import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { NotificationType, SubscriptionStatus } from 'common/enums';
import { ModalWarning } from 'common/modal-warning';
import { appActions, AppContext } from 'context/app-context';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { goToPage, RouteName } from 'routes';
import styles from './draft-vault-checkout.module.scss';

// After Stripe sends the user back paid, the webhook that opens the vault can
// land a moment later: re-check this often, for this long.
const POLL_MS = 2000;
const POLL_FOR_MS = 60000;

type Phase = 'unpaid' | 'confirming' | 'slow';

interface DraftVaultCheckoutProps {
  vaultId: number;
  title: string;
  // Payment went through and the vault is open: reload it.
  onPaid: () => void;
}

// A storage-plan vault whose Checkout wasn't paid (awaiting_checkout): a locked
// draft. Its storage, sharing and the invitations wait for the payment.
export const DraftVaultCheckout = ({
  vaultId,
  title,
  onPaid,
}: DraftVaultCheckoutProps) => {
  const { dispatch } = useContext(AppContext);
  const returnedPaid =
    new URLSearchParams(useLocation().search).get('checkout') === 'success';
  const [phase, setPhase] = useState<Phase>(
    returnedPaid ? 'confirming' : 'unpaid',
  );
  const [opening, setOpening] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const notifyError = (message: string) =>
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title: 'Vault draft',
        message,
        type: NotificationType.Error,
      }),
    });

  // Never offer to pay again while a payment may still be confirming: that
  // could charge twice.
  useEffect(() => {
    if (!returnedPaid) return undefined;

    const startedAt = Date.now();
    const timer = setInterval(async () => {
      try {
        const vault = await VaultsController.getVault(vaultId);
        if (vault.subscriptionStatus !== SubscriptionStatus.awaitingCheckout) {
          clearInterval(timer);
          onPaid();
          return;
        }
      } catch {
        // Try again on the next tick.
      }
      if (Date.now() - startedAt > POLL_FOR_MS) {
        clearInterval(timer);
        setPhase('slow');
      }
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [returnedPaid, vaultId]);

  const completePayment = async () => {
    setOpening(true);
    try {
      const checkoutUrl = await VaultsController.createCheckoutSession(vaultId);
      // Full-page navigation: this leaves the app for Stripe.
      window.location.assign(checkoutUrl);
    } catch {
      setOpening(false);
      notifyError("We couldn't start checkout. Please try again.");
    }
  };

  const deleteDraft = async () => {
    setDeleting(true);
    try {
      await VaultsController.deleteVault(vaultId);
      goToPage(RouteName.Vaults);
    } catch {
      setDeleting(false);
      setConfirmDelete(false);
      notifyError("We couldn't delete the draft. Please try again.");
    }
  };

  const renderUnpaid = () => (
    <>
      <h1 className={styles.title}>
        Finish setting up {title || 'your vault'}
      </h1>
      <p className={styles.text}>
        Your vault is saved as a draft. Once payment is complete it opens with
        its storage, and the people you added get their invitations.
      </p>
      <p className={styles.note}>Unpaid drafts are deleted after 7 days.</p>
      <div className={styles.actions}>
        <Button
          buttonStyle={ButtonStyle.Primary}
          buttonSize={ButtonSize.Medium}
          onClick={completePayment}
          disabled={opening}
        >
          {opening ? 'Opening checkout…' : 'Complete payment'}
        </Button>
        <Button
          buttonStyle={ButtonStyle.SecondaryGray}
          buttonSize={ButtonSize.Medium}
          onClick={() => setConfirmDelete(true)}
          disabled={opening}
        >
          Delete draft
        </Button>
      </div>
    </>
  );

  const renderConfirming = () => (
    <>
      <h1 className={styles.title}>Confirming your payment…</h1>
      <p className={styles.text}>
        This takes a few seconds. {title || 'Your vault'} opens as soon as it is
        confirmed.
      </p>
    </>
  );

  const renderSlow = () => (
    <>
      <h1 className={styles.title}>Your payment is still being confirmed</h1>
      <p className={styles.text}>
        This is taking longer than usual. Refresh this page in a minute; there
        is no need to pay again.
      </p>
    </>
  );

  const renderPhase = () => {
    if (phase === 'confirming') return renderConfirming();
    if (phase === 'slow') return renderSlow();
    return renderUnpaid();
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {renderPhase()}
        <Button
          buttonStyle={ButtonStyle.LinkGray}
          buttonSize={ButtonSize.Medium}
          onClick={() => goToPage(RouteName.Vaults)}
          className={styles.back}
        >
          Back to my vaults
        </Button>
      </div>
      {confirmDelete && (
        <ModalWarning
          title="Delete draft"
          content="This deletes the vault and its pending invitations. It can't be undone."
          successButtonText={deleting ? 'Deleting…' : 'Delete draft'}
          successStyleButton={ButtonStyle.RedFilled}
          closeFn={() => setConfirmDelete(false)}
          successFn={deleteDraft}
        />
      )}
    </div>
  );
};
